import React, { useEffect, useMemo, useRef, useState } from 'react';
import { auth } from '../../../firebase';
import './TemplateGallery.css';

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

const todayIso = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Asks for a template's fill-in fields before rendering (BBD-107).
 *
 * Field types: text, date (defaults to today), choice, person. In a
 * workspace, person fields search members through the mention-users
 * endpoint; for personal documents they are free text.
 *
 * Props:
 *   template     – template object with `fields` (and name for the title)
 *   workspaceId  – optional, enables member search for person fields
 *   submitLabel  – button text, e.g. "Create" / "Insert"
 *   onSubmit(values) – called with { key: value }
 *   onCancel()
 */
const TemplateFieldsDialog = ({ template, workspaceId, submitLabel = 'Continue', busy = false, error = '', onSubmit, onCancel }) => {
    const fields = useMemo(() => template?.fields || [], [template]);
    const [values, setValues] = useState({});
    const [touched, setTouched] = useState(false);
    const firstInputRef = useRef(null);

    useEffect(() => {
        const initial = {};
        fields.forEach((f) => {
            if (f.default) initial[f.key] = f.default;
            else if (f.type === 'date') initial[f.key] = todayIso();
            else if (f.type === 'choice') initial[f.key] = f.options?.[0] || '';
            else initial[f.key] = '';
        });
        setValues(initial);
        setTouched(false);
    }, [fields]);

    useEffect(() => {
        const t = setTimeout(() => firstInputRef.current?.focus(), 30);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onCancel?.();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onCancel]);

    const missing = fields.filter((f) => f.required && !(values[f.key] || '').trim()).map((f) => f.label || f.key);

    const handleSubmit = (e) => {
        e.preventDefault();
        setTouched(true);
        if (missing.length || busy) return;
        onSubmit?.(values);
    };

    const setValue = (key, value) => setValues((v) => ({ ...v, [key]: value }));

    return (
        <div className="TplGallery__overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}>
            <form className="TplFields__modal" onSubmit={handleSubmit} role="dialog" aria-modal="true" aria-label="Template details">
                <div className="TplGallery__header">
                    <h3 className="TplGallery__title">
                        {template?.name || 'Template'}
                    </h3>
                    <button type="button" className="TplGallery__close" onClick={onCancel} aria-label="Close">×</button>
                </div>
                <div className="TplFields__body">
                    {fields.map((f, i) => (
                        <div className="TplFields__field" key={f.key}>
                            <label htmlFor={`tpl-field-${f.key}`}>
                                {f.label || f.key}
                                {f.required && <span className="TplFields__required" aria-hidden="true">*</span>}
                            </label>
                            {f.type === 'choice' ? (
                                <select
                                    id={`tpl-field-${f.key}`}
                                    ref={i === 0 ? firstInputRef : undefined}
                                    value={values[f.key] || ''}
                                    onChange={(e) => setValue(f.key, e.target.value)}
                                >
                                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                            ) : f.type === 'person' && workspaceId ? (
                                <PersonInput
                                    id={`tpl-field-${f.key}`}
                                    inputRef={i === 0 ? firstInputRef : undefined}
                                    workspaceId={workspaceId}
                                    value={values[f.key] || ''}
                                    onChange={(v) => setValue(f.key, v)}
                                />
                            ) : (
                                <input
                                    id={`tpl-field-${f.key}`}
                                    ref={i === 0 ? firstInputRef : undefined}
                                    type={f.type === 'date' ? 'date' : 'text'}
                                    value={values[f.key] || ''}
                                    maxLength={500}
                                    placeholder={f.type === 'person' ? 'Name' : ''}
                                    onChange={(e) => setValue(f.key, e.target.value)}
                                />
                            )}
                        </div>
                    ))}
                    {touched && missing.length > 0 && (
                        <div className="TplGallery__error" style={{ padding: 0 }}>Please fill in: {missing.join(', ')}</div>
                    )}
                    {error && <div className="TplGallery__error" style={{ padding: 0 }}>{error}</div>}
                </div>
                <div className="TplGallery__previewFoot">
                    <button type="button" className="TplGallery__btn" onClick={onCancel} disabled={busy}>Cancel</button>
                    <button type="submit" className="TplGallery__btn TplGallery__btn--primary" disabled={busy}>
                        {busy ? 'Working…' : submitLabel}
                    </button>
                </div>
            </form>
        </div>
    );
};

/** Free text with a member dropdown fed by the workspace mention search. */
const PersonInput = ({ id, inputRef, workspaceId, value, onChange }) => {
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const timer = useRef(null);

    useEffect(() => () => clearTimeout(timer.current), []);

    const search = (q) => {
        clearTimeout(timer.current);
        if (!q || q.trim().length < 1) {
            setResults([]);
            setOpen(false);
            return;
        }
        timer.current = setTimeout(async () => {
            try {
                const user = auth.currentUser;
                const token = user ? await user.getIdToken() : null;
                if (!token) return;
                const res = await fetch(`${API_BASE}/workspaces/${workspaceId}/mention-users/?q=${encodeURIComponent(q.trim())}`, {
                    headers: { Authorization: token, 'auth-token': `Bearer ${token}`, 'requesting-user-uid': user.uid },
                });
                if (!res.ok) return;
                const data = await res.json();
                setResults(Array.isArray(data) ? data.slice(0, 8) : []);
                setActive(0);
                setOpen(true);
            } catch (e) {
                /* search is a convenience */
            }
        }, 200);
    };

    const pick = (u) => {
        onChange(u.name || u.email || '');
        setOpen(false);
        setResults([]);
    };

    const onKeyDown = (e) => {
        if (!open || !results.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); pick(results[active]); }
        else if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    };

    return (
        <div className="TplFields__person">
            <input
                id={id}
                ref={inputRef}
                type="text"
                value={value}
                autoComplete="off"
                placeholder="Search members or type a name"
                onChange={(e) => { onChange(e.target.value); search(e.target.value); }}
                onKeyDown={onKeyDown}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onFocus={() => results.length && setOpen(true)}
            />
            {open && results.length > 0 && (
                <ul className="TplFields__personList" role="listbox">
                    {results.map((u, i) => (
                        <li
                            key={u.uid || u.email}
                            role="option"
                            aria-selected={i === active}
                            className={i === active ? 'is-active' : ''}
                            onMouseDown={(e) => { e.preventDefault(); pick(u); }}
                        >
                            {u.name || u.email}
                            {u.name && u.email && <span className="TplFields__personMeta">{u.email}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default TemplateFieldsDialog;
